'use client';

import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { TIMING } from '../lib/constants';
import { ContinueButton } from './ui/ContinueButton';

interface ReflectionCardProps {
  reflectionText: string;
  onContinue: () => void;
  /**
   * Card-level affordance rendered as the card's caption: tucked to the box's
   * bottom edge (8px, right-aligned), OUTSIDE the box so box purity holds.
   * This is where "Adjust" lives (action-hierarchy re-tier, 2026-07-18)
   * — a whisper attached to its referent, not a sibling of Continue in the
   * screen's action stack.
   */
  cardCaption?: React.ReactNode;
  scaleSlot?: React.ReactNode;
  continueDisabled?: boolean;
  continueLabel?: string;
  /**
   * Parent-owned reveal latch pass-through (Codex r3 P1): lets the screen
   * flip Continue and the caption together so no action ever gets a
   * solo-interactive window. Omitted → ContinueButton's internal latch.
   */
  continueRevealed?: boolean;
  onContinueRevealed?: () => void;
  /**
   * Where Continue goes. Absent (the default): under the card, as on every
   * standalone screen. In a session's exchange screen the answer dock owns the
   * bottom of the screen and Continue lives there, in the same place as the
   * microphone (owner decision 2026-10-05): the dock hands its slot element
   * here and Continue is portalled into it. `null` means the dock is resting
   * (the card is still landing) or its slot has not mounted yet, so Continue
   * waits rather than flashing under the card.
   */
  continueContainer?: HTMLElement | null;
  /**
   * `screen` (default): the card owns its screen, with screen padding.
   * `dock`: it sits in the exchange screen's middle, so it takes the dock's
   * narrower inset and no vertical padding of its own.
   */
  inset?: 'screen' | 'dock';
}

export function ReflectionCard({
  reflectionText,
  onContinue,
  cardCaption,
  scaleSlot,
  continueDisabled = false,
  continueLabel = 'Continue',
  continueRevealed,
  onContinueRevealed,
  continueContainer,
  inset = 'screen',
}: ReflectionCardProps) {
  const continueButton = (
    <ContinueButton
      onTap={onContinue}
      disabled={continueDisabled}
      label={continueLabel}
      revealed={continueRevealed}
      onRevealed={onContinueRevealed}
    />
  );

  return (
    <div
      className={`flex flex-col items-center gap-6 ${inset === 'dock' ? 'px-4' : 'px-6 py-8'}`}
      data-ph-no-capture
      aria-live="polite"
    >
      {/* Card group: box + caption share the card's width so the caption
          right-aligns to the box edge. gap-2 = element spacing (caption is
          sub-text under its parent, not a stack sibling). */}
      <div className="flex w-full max-w-md flex-col items-end gap-2">
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: TIMING.CARD_ENTER / 1000, ease: 'easeOut' }}
          style={{ boxShadow: 'var(--shadow-sm)' }}
          data-testid="reflection-card"
          className="w-full rounded-[var(--radius-lg)] bg-reflection-bg border border-reflection-border p-6"
          role="status"
        >
          <motion.p
            data-testid="reflection-text"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: TIMING.CARD_TEXT_DELAY / 1000, duration: 0.4 }}
            className="font-body text-base font-normal leading-[1.65] text-text-primary [text-wrap:pretty]"
            data-reflection-text
          >
            {reflectionText}
          </motion.p>
        </motion.div>
        {cardCaption}
      </div>

      {scaleSlot}

      {continueContainer === undefined ? (
        // mt-2 on top of the column's gap-6 = 32px before the primary CTA (the
        // design system's before-primary rule; the column gap alone is sibling
        // spacing).
        <div className="mt-2">{continueButton}</div>
      ) : continueContainer ? (
        createPortal(continueButton, continueContainer)
      ) : null}
    </div>
  );
}
